import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  MagnifyingGlassIcon, 
  DocumentTextIcon, 
  CloudArrowUpIcon,
  UserCircleIcon,
  TagIcon,
  CodeBracketIcon,
  BookOpenIcon,
  ShieldCheckIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

const Docs: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'browsing' | 'uploading' | 'agents'>('browsing');

  useEffect(() => {
    const tabOption = searchParams.get('tab');
    if (tabOption === 'agents') {
      setActiveTab('agents');
    } else if (tabOption === 'uploading') {
      setActiveTab('uploading');
    }
  }, [searchParams]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 min-h-[calc(100vh-64px)]">
      <div className="mb-10 text-center sm:text-left">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Documentation</h1>
        <p className="text-gray-600 text-lg max-w-3xl">
          Welcome to the RI-SCALE Model Hub user guide. Learn how to discover standardized AI models 
          or contribute your own to the community.
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className="flex justify-center sm:justify-start mb-8 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('browsing')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors
              ${activeTab === 'browsing'
                ? 'border-ri-orange text-ri-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            Browsing Models
          </button>
          <button
            onClick={() => setActiveTab('uploading')}
            className={`
              whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors
              ${activeTab === 'uploading'
                ? 'border-ri-orange text-ri-orange'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
            `}
          >
            <CloudArrowUpIcon className="h-5 w-5" />
            Uploading & Sharing
          </button>
               <button
                  onClick={() => setActiveTab('agents')}
                  className={`
                     whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors
                     ${activeTab === 'agents'
                        ? 'border-ri-orange text-ri-orange'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
               >
                  <CpuChipIcon className="h-5 w-5" />
                  Agents
               </button>
        </nav>
      </div>

      {/* Content Area */}
      <div className="grid gap-8">
        {activeTab === 'browsing' ? (
          <div className="space-y-8 fade-in">
             <section className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm">
                <div className="flex items-start gap-4">
                   <div className="hidden sm:flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-50 text-ri-orange">
                      <MagnifyingGlassIcon className="h-6 w-6" />
                   </div>
                   <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">Search and Discovery</h2>
                      <p className="text-gray-600 mb-6">
                         The Model Hub provides a powerful search interface to help you find the right model for your research.
                      </p>
                      <ul className="space-y-4">
                         <li className="flex gap-3">
                            <div className="flex-none pt-1">
                               <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-600">1</span>
                            </div>
                            <div>
                               <strong className="font-medium text-gray-900">Smart Search</strong>
                               <p className="text-gray-500 text-sm mt-0.5">
                                  Use the search bar on the home page to find models by name, description, or keywords.
                               </p>
                            </div>
                         </li>
                         <li className="flex gap-3">
                            <div className="flex-none pt-1">
                               <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-600">2</span>
                            </div>
                            <div>
                               <strong className="font-medium text-gray-900">Tag Filtering</strong>
                               <p className="text-gray-500 text-sm mt-0.5">
                                  Filter models by specific tasks (e.g., <span className="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-gray-700">segmentation</span>, <span className="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-gray-700">restoration</span>) or modalities.
                               </p>
                            </div>
                         </li>
                      </ul>
                   </div>
                </div>
             </section>

             <section className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm">
                <div className="flex items-start gap-4">
                   <div className="hidden sm:flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-50 text-ri-orange">
                      <DocumentTextIcon className="h-6 w-6" />
                   </div>
                   <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">Model Details</h2>
                      <p className="text-gray-600 mb-4">
                         Clicking on a model card opens its detailed view, where you can access:
                      </p>
                      <div className="grid sm:grid-cols-2 gap-4">
                         <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                             <h4 className="font-medium text-gray-900 mb-1">Metadata</h4>
                             <p className="text-sm text-gray-500">Full description, authors, license, and citations.</p>
                         </div>
                         <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                             <h4 className="font-medium text-gray-900 mb-1">Files</h4>
                             <p className="text-sm text-gray-500">Direct download links for model weights and configuration.</p>
                         </div>
                         <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                             <h4 className="font-medium text-gray-900 mb-1">Versioning</h4>
                             <p className="text-sm text-gray-500">Access to previous versions of the model.</p>
                         </div>
                      </div>
                   </div>
                </div>
             </section>
          </div>
        ) : activeTab === 'uploading' ? (
          <div className="space-y-8 fade-in">
             <section className="bg-gray-900 text-white rounded-xl p-8 shadow-md">
                 <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                     <div>
                        <h2 className="text-2xl font-bold mb-2">Contribute to the Hub</h2>
                        <p className="text-gray-300">
                           Share your AI models with the research community. It only takes a few steps.
                        </p>
                     </div>
                     <Link to="/upload" className="px-6 py-3 bg-ri-orange hover:bg-white hover:text-ri-orange text-white font-semibold rounded-lg transition-colors whitespace-nowrap">
                        Start Uploading
                     </Link>
                 </div>
             </section>

             <div className="grid sm:grid-cols-3 gap-6">
                <div className="border border-gray-200 rounded-xl p-6 hover:border-ri-orange/50 transition-colors">
                   <div className="h-10 w-10 bg-gray-100 text-gray-600 rounded-lg flex items-center justify-center mb-4">
                      <UserCircleIcon className="h-6 w-6" />
                   </div>
                   <h3 className="font-semibold text-gray-900 mb-2">1. Authenticate</h3>
                   <p className="text-gray-600 text-sm">
                      Log in using your RI-SCALE credentials via Hypha to access upload features.
                   </p>
                </div>
                <div className="border border-gray-200 rounded-xl p-6 hover:border-ri-orange/50 transition-colors">
                   <div className="h-10 w-10 bg-gray-100 text-gray-600 rounded-lg flex items-center justify-center mb-4">
                      <TagIcon className="h-6 w-6" />
                   </div>
                   <h3 className="font-semibold text-gray-900 mb-2">2. Add Metadata</h3>
                   <p className="text-gray-600 text-sm">
                      Provide a name, description, and relevant tags to help others discover your model.
                   </p>
                </div>
                <div className="border border-gray-200 rounded-xl p-6 hover:border-ri-orange/50 transition-colors">
                   <div className="h-10 w-10 bg-gray-100 text-gray-600 rounded-lg flex items-center justify-center mb-4">
                      <CloudArrowUpIcon className="h-6 w-6" />
                   </div>
                   <h3 className="font-semibold text-gray-900 mb-2">3. Upload Files</h3>
                   <p className="text-gray-600 text-sm">
                      Upload your model weights, configuration files, and sample data.
                   </p>
                </div>
             </div>

             {/* Publication workflow — new section for the manifest.published change */}
             <section id="publishing" className="bg-white border-2 border-orange-300 rounded-xl p-6 sm:p-8 shadow-sm">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                   <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-500 text-white">
                      API CHANGE · JUNE 2026
                   </span>
                   <h2 className="text-xl font-semibold text-gray-900 m-0">4. Publish to the catalogue</h2>
                </div>
                <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                   The public catalogue at <a href="https://modelhub.riscale.eu" className="text-ri-orange underline">modelhub.riscale.eu</a> now only shows artifacts the contributor has <strong>explicitly published</strong>. New uploads default to <strong>draft</strong> and stay hidden from the catalogue (visible only to their owner on the <Link to="/my-artifacts" className="text-ri-orange underline">My Artifacts</Link> page) until you flip <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">manifest.published</code> to the string <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">"true"</code>.
                </p>
                <p className="text-gray-700 mb-5 text-sm">
                   If your previous uploads aren't appearing in the catalogue, this is almost certainly why. Two ways forward:
                </p>

                <div className="grid lg:grid-cols-2 gap-5 mb-5">
                   <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                         <h3 className="font-semibold text-gray-900 text-sm m-0">Mode A · Express (one call)</h3>
                         <p className="text-xs text-gray-500 mt-0.5 m-0">Set the flag at create time — appears in catalogue immediately.</p>
                      </div>
                      <pre className="bg-gray-900 text-gray-100 text-xs font-mono p-4 m-0 overflow-x-auto whitespace-pre-wrap break-all">
{`await am.create(
    alias=...,
    parent_id="ri-scale/ai-model-hub",
    type="model",
    manifest={
        ...,
        "published": "true",   # ← add this
    },
    config={"storage": "git"},
    stage=False,
)`}
                      </pre>
                   </div>

                   <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                         <h3 className="font-semibold text-gray-900 text-sm m-0">Mode B · Safe (two-step)</h3>
                         <p className="text-xs text-gray-500 mt-0.5 m-0">Create as draft, push files, then publish explicitly. Recommended.</p>
                      </div>
                      <pre className="bg-gray-900 text-gray-100 text-xs font-mono p-4 m-0 overflow-x-auto whitespace-pre-wrap break-all">
{`# Existing create() — no change
art = await am.create(alias=..., manifest={...},
                      config={"storage": "git"}, stage=False)

# git push files... then explicitly publish:
current = await am.read(artifact_id=art.id)
m = {**current["manifest"], "published": "true"}
await am.edit(artifact_id=art.id, manifest=m, stage=True)
await am.commit(artifact_id=art.id)`}
                      </pre>
                   </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-5 text-sm">
                   <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <strong className="text-amber-900 block mb-1">⚠ Value gotcha</strong>
                      <span className="text-amber-900">
                         Use the literal string <code className="text-xs bg-white px-1 py-0.5 rounded">"true"</code>, not the boolean. The catalogue filter coerces to string and a JSON boolean silently matches zero items.
                      </span>
                   </div>
                   <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <strong className="text-blue-900 block mb-1">ℹ <code>am.publish()</code> is different</strong>
                      <span className="text-blue-900">
                         <code className="text-xs bg-white px-1 py-0.5 rounded">am.publish()</code> archives to Zenodo for a DOI — it does NOT control catalogue visibility.
                      </span>
                   </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 items-start">
                   <Link to="/my-artifacts" className="inline-flex items-center gap-1.5 px-4 py-2 bg-ri-orange text-white text-sm font-medium rounded-full hover:bg-orange-600 transition-colors">
                      Manage in My Artifacts
                   </Link>
                   <a href="https://modelhub.riscale.eu/SKILL.md" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-full hover:border-ri-orange hover:text-ri-orange transition-colors">
                      Full agent skill (SKILL.md) →
                   </a>
                </div>
             </section>

             <section className="bg-orange-50 border border-orange-100 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                   <ShieldCheckIcon className="h-5 w-5 text-ri-orange" />
                   Quality & Standards
                </h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                   All uploaded models undergo a validation process to ensure they contain necessary metadata and file structures.
                   We encourage providing comprehensive descriptions to foster trust and reproducibility.
                </p>
             </section>
          </div>
        ) : (
          <div className="space-y-8 fade-in">
             <section className="bg-white border border-gray-200 rounded-xl p-6 sm:p-8 shadow-sm">
                <div className="flex items-start gap-4">
                   <div className="hidden sm:flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-orange-50 text-ri-orange">
                      <CpuChipIcon className="h-6 w-6" />
                   </div>
                   <div>
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">AI Agents</h2>
                      <p className="text-gray-600 mb-6">
                         Interact with specialized AI agents to navigate services and resources. The core agent is the BioImage Archive Finder.
                      </p>
                      
                      <div className="bg-gray-50 rounded-lg p-5 border border-gray-100 mb-6">
                         <h3 className="font-semibold text-gray-900 mb-2">BioImage Archive Finder</h3>
                         <code className="text-xs bg-gray-200 px-1.5 py-0.5 rounded text-gray-700 block w-fit mb-3">
                           hypha-agents/grammatical-deduction-bury-enormously
                         </code>
                         <p className="text-sm text-gray-700 mb-4">
                           This agent helps you discover datasets in BioImage Archive.
                         </p>
                         
                         <strong className="text-sm font-medium text-gray-900 block mb-2">Capabilities:</strong>
                         <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 ml-1">
                            <li><strong>Author Queries:</strong> Search dataset.</li>
                            <li><strong>Genome Queries:</strong> Locate specific techniques (e.g., "super-resolution microscopy").</li>
                            <li><strong>Full-Text Search:</strong> Broad queries across all metadata (e.g., "datasets related to cancer research").</li>
                         </ul>
                      </div>

                      <div className="space-y-4">
                         <h4 className="font-semibold text-gray-900 text-sm">How it Works</h4>
                         <p className="text-sm text-gray-600">
                            The agent uses two endpoints to query the BioImage Archive:
                         </p>
                         <ul className="grid sm:grid-cols-2 gap-3 text-sm text-gray-600">
                            <li className="flex items-center gap-2">
                               <span className="w-1.5 h-1.5 rounded-full bg-ri-orange/60"></span>
                               https://beta.bioimagearchive.org/search/search/fts: Search dataset
                            </li>
                            <li className="flex items-center gap-2">
                               <span className="w-1.5 h-1.5 rounded-full bg-ri-orange/60"></span>
                               https://beta.bioimagearchive.org/search/search/fts/image: Search images
                            </li>
                         </ul>

                         <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                            <h5 className="font-semibold text-gray-900 text-sm mb-2">Architecture Principle</h5>
                            <p className="text-sm text-gray-700 leading-relaxed">
                              Agent-specific behavior belongs in the agent startup script, not in the generic chat runner.
                              For BioImage Finder, compact search payload shaping and fallback dataset summary formatting are implemented in the startup script to keep the frontend agent-agnostic.
                            </p>
                         </div>
                      </div>
                   </div>
                </div>
             </section>
          </div>
        )}
      </div>

      {/* Footer / Additional Resources (Visible on both tabs) */}
      <div className="mt-16 pt-10 border-t border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Additional Resources</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
           <Link to="/api" className="group p-4 border border-gray-200 rounded-lg hover:border-ri-orange hover:shadow-sm transition-all">
              <div className="flex items-center justify-between mb-2">
                 <CodeBracketIcon className="h-6 w-6 text-gray-400 group-hover:text-ri-orange transition-colors" />
                 <span className="text-ri-orange text-sm opacity-0 group-hover:opacity-100 transition-opacity">View &rarr;</span>
              </div>
              <h3 className="font-medium text-gray-900">API Documentation</h3>
              <p className="text-sm text-gray-500 mt-1">Programmatic access guide.</p>
           </Link>

           <Link to="/about" className="group p-4 border border-gray-200 rounded-lg hover:border-ri-orange hover:shadow-sm transition-all">
              <div className="flex items-center justify-between mb-2">
                 <BookOpenIcon className="h-6 w-6 text-gray-400 group-hover:text-ri-orange transition-colors" />
                 <span className="text-ri-orange text-sm opacity-0 group-hover:opacity-100 transition-opacity">Read &rarr;</span>
              </div>
              <h3 className="font-medium text-gray-900">About Project</h3>
              <p className="text-sm text-gray-500 mt-1">Background and partners.</p>
           </Link>

           <Link to="/agents" className="group p-4 border border-gray-200 rounded-lg hover:border-ri-orange hover:shadow-sm transition-all">
              <div className="flex items-center justify-between mb-2">
                 <CpuChipIcon className="h-6 w-6 text-gray-400 group-hover:text-ri-orange transition-colors" />
                 <span className="text-ri-orange text-sm opacity-0 group-hover:opacity-100 transition-opacity">Open &rarr;</span>
              </div>
              <h3 className="font-medium text-gray-900">Agents</h3>
              <p className="text-sm text-gray-500 mt-1">Open the interactive agent chat interface.</p>
           </Link>
        </div>
      </div>
    </div>
  );
};

export default Docs;